import React, { useState, useEffect } from "react";
import {
  Box,
  Tabs,
  Tab,
  Card,
  CardContent,
  Typography,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Chip,
} from "@mui/material";
import ReactDatePicker from "react-datepicker";
import {
  Dashboard as DashboardIcon,
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  Work as WorkIcon,
  LiveTv as LiveIcon,
  Refresh as RefreshIcon,
  MoreVert as MoreVertIcon,
  Download as DownloadIcon,
  Fullscreen as FullscreenIcon,
} from "@mui/icons-material";
import RevenueCharts from "../../components/charts/RevenueCharts";
import UserAnalyticsCharts from "../../components/charts/UserAnalyticsCharts";
import ConsultantPerformanceCharts from "../../components/charts/ConsultantPerformanceCharts";
import RealTimeCharts from "../../components/charts/RealTimeCharts";
import { fetchQuickStatsApi } from "../../services/enhancedDashboard";
import { toastMessage } from "../../utils/toastMessage";

const AnalyticsDashboard = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [startDate, setStartDate] = useState(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)); // 30 days ago
  const [endDate, setEndDate] = useState(new Date());
  const [quickStats, setQuickStats] = useState({
    activeUsers: 0,
    onlineConsultants: 0,
    activeSessions: 0,
    todayRevenue: 0,
    totalRevenue: 0,
    totalTransactions: 0
  });
  const [loading, setLoading] = useState(false);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleRefresh = () => {
    setRefreshKey(prev => prev + 1);
    fetchQuickStats();
    handleMenuClose();
  };

  const fetchQuickStats = async () => {
    try {
      setLoading(true);
      const { data } = await fetchQuickStatsApi({ timeRange: 'today' });
      if (data.code === 200) {
        setQuickStats(data.result);
      } else {
        toastMessage(data.message, "error");
      }
    } catch (error) {
      console.error("Error fetching quick stats:", error);
      toastMessage("Error fetching quick stats", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQuickStats();
  }, []);

  const handleExport = () => {
    // Implement export functionality
    console.log("Exporting dashboard data...");
    handleMenuClose();
  };

  const handleFullscreen = () => {
    // Implement fullscreen functionality
    console.log("Toggling fullscreen...");
    handleMenuClose();
  };

  const tabs = [
    {
      label: "Overview",
      icon: <DashboardIcon />,
      component: <RevenueCharts key={refreshKey} startDate={startDate} endDate={endDate} />,
    },
    {
      label: "User Analytics",
      icon: <PeopleIcon />,
      component: <UserAnalyticsCharts key={refreshKey} startDate={startDate} endDate={endDate} />,
    },
    {
      label: "Consultant Performance",
      icon: <WorkIcon />,
      component: <ConsultantPerformanceCharts key={refreshKey} startDate={startDate} endDate={endDate} />,
    },
    {
      label: "Real-time Monitoring",
      icon: <LiveIcon />,
      component: <RealTimeCharts key={refreshKey} />,
    },
  ];

  const quickStatsData = [
    { title: "Total Revenue", value: `₹${quickStats.totalRevenue?.toLocaleString() || 0}`, change: "+12.5%", color: "#1976d2" },
    { title: "Active Users", value: quickStats.activeUsers?.toLocaleString() || 0, change: "+8.2%", color: "#2e7d32" },
    { title: "Online Consultants", value: quickStats.onlineConsultants || 0, change: "Live", color: "#ed6c02" },
    { title: "Active Sessions", value: quickStats.activeSessions || 0, change: "Live", color: "#f44336" },
  ];

  return (
    <div className="WrapperArea">
      <div className="WrapperBox">
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h4" component="h1" sx={{ fontWeight: "bold", color: "#1976d2" }}>
              Analytics Dashboard
            </Typography>
            <Box display="flex" alignItems="center" gap={1}>
              <Chip
                label="Live"
                color="success"
                size="small"
                icon={<LiveIcon />}
              />
              <IconButton onClick={handleMenuOpen} color="primary">
                <MoreVertIcon />
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
              >
                <MenuItem onClick={handleRefresh}>
                  <RefreshIcon sx={{ mr: 1 }} />
                  Refresh Data
                </MenuItem>
                <MenuItem onClick={handleExport}>
                  <DownloadIcon sx={{ mr: 1 }} />
                  Export Report
                </MenuItem>
                <MenuItem onClick={handleFullscreen}>
                  <FullscreenIcon sx={{ mr: 1 }} />
                  Fullscreen
                </MenuItem>
              </Menu>
            </Box>
          </Box>

          {/* Quick Stats */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {quickStatsData.map((stat, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card sx={{ height: "100%" }}>
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Box>
                        <Typography color="textSecondary" gutterBottom variant="h6">
                          {stat.title}
                        </Typography>
                        <Typography variant="h4" component="h2" color={stat.color}>
                          {stat.value}
                        </Typography>
                        <Chip
                          label={stat.change}
                          size="small"
                          color={stat.change.includes("+") ? "success" : stat.change.includes("-") ? "error" : "default"}
                          sx={{ mt: 1 }}
                        />
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Date Range Controls */}
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="h6">Date Range</Typography>
                <Box display="flex" gap={2} alignItems="center">
                  <ReactDatePicker
                    selected={startDate}
                    onChange={(date) => setStartDate(date)}
                    className="form-control"
                    dateFormat="dd-MMM-yyyy"
                    placeholderText="Start Date"
                    maxDate={endDate}
                  />
                  <Typography variant="body2">to</Typography>
                  <ReactDatePicker
                    selected={endDate}
                    onChange={(date) => setEndDate(date)}
                    className="form-control"
                    dateFormat="dd-MMM-yyyy"
                    placeholderText="End Date"
                    minDate={startDate}
                    maxDate={new Date()}
                  />
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Navigation Tabs */}
          <Card>
            <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  "& .MuiTab-root": {
                    minHeight: 48,
                    textTransform: "none",
                    fontWeight: 500,
                  },
                }}
              >
                {tabs.map((tab, index) => (
                  <Tab
                    key={index}
                    label={tab.label}
                    icon={tab.icon}
                    iconPosition="start"
                    sx={{ minHeight: 48 }}
                  />
                ))}
              </Tabs>
            </Box>
          </Card>
        </Box>

        {/* API Test Component */}

        {/* Tab Content */}
        <Box sx={{ mt: 2 }}>
          {tabs[activeTab].component}
        </Box>

        {/* Footer Info */}
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="body2" color="textSecondary">
                Last updated: {new Date().toLocaleString()}
              </Typography>
              <Box display="flex" gap={2}>
                <Typography variant="body2" color="textSecondary">
                  Data refresh: Every 30 seconds
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Status: <Chip label="Operational" color="success" size="small" />
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AnalyticsDashboard;

